// Премиальные SVG-иконки качества хода («монетки» в духе chess.com, но свои):
// градиентная заливка, верхний блик, мягкая тень, белый ободок-гало и жирный
// символ. Чистый вектор — резкие на любом зуме, без растровых ассетов.
import { Q_COLOR, type MoveQuality } from './classify';

/** Символ в кружке. Двойные (!! ?? ?!) рисуем мельче, чтобы влезли в круг. */
const SYMBOL: Record<MoveQuality, string> = {
  brilliant: '!!', great: '!', best: '★', excellent: '✦', good: '✓', book: '',
  forced: '=', inaccuracy: '?!', mistake: '?', miss: '✕', blunder: '??',
};

function shade(hex: string, pct: number): string {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  const t = pct < 0 ? 0 : 255;
  const a = Math.abs(pct) / 100;
  r = Math.round((t - r) * a + r);
  g = Math.round((t - g) * a + g);
  b = Math.round((t - b) * a + b);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/** Тёмный символ на светлых монетках (жёлтая/салатовая), белый — на остальных. */
function symbolFill(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.62 ? '#2b2410' : '#ffffff';
}

/** Раскрытая книжка для «book» — рисуем path, чтобы не тащить цветной emoji. */
function bookPath(fill: string): string {
  return (
    `<path d="M12 7.6c-1.5-1-3.4-1.3-5.2-1V16c1.8-.3 3.7 0 5.2 1 1.5-1 3.4-1.3 5.2-1V6.6c-1.8-.3-3.7 0-5.2 1z" ` +
    `fill="${fill}" stroke="${fill}" stroke-width="0.6" stroke-linejoin="round"/>` +
    `<line x1="12" y1="7.6" x2="12" y2="17" stroke="${shade(fill, -55)}" stroke-width="0.8"/>`
  );
}

/** SVG-иконка качества хода заданного размера (px). Крупный жирный символ,
 *  высокий контраст, минимум декора — читается даже на 16px. */
export function qualityIconSvg(quality: MoveQuality, size = 18): string {
  const base = Q_COLOR[quality];
  const light = shade(base, 16);
  const dark = shade(base, -16);
  const sym = SYMBOL[quality];
  const fill = symbolFill(base);
  // тонкая контурная обводка символа противоположной яркости — символ «вырезан»
  const halo = fill === '#ffffff' ? shade(base, -42) : '#ffffff';
  const gid = `qg-${quality}`;
  const sid = `qs-${quality}`;
  const double = sym.length > 1;
  // одиночный «!» (great) великоват и читается бруском → чуть мельче; «★ ✓ ✦ ✕ ? =» ок крупными
  const fontSize = double ? 13 : sym === '!' ? 15 : 18;
  const letter = double ? 0.5 : 0;
  // вертикальный центр символа в кружке: baseline = центр(12) + ~0.34·кегль (без
  // dominant-baseline — оно ненадёжно по движкам и даёт смещение вниз).
  const baseY = (12 + fontSize * 0.34).toFixed(2);
  const inner =
    quality === 'book'
      ? bookPath(fill)
      : `<text x="12" y="${baseY}" text-anchor="middle" ` +
        `font-family="'Helvetica Neue',Arial,sans-serif" font-weight="900" font-size="${fontSize}" ` +
        `letter-spacing="${letter}" paint-order="stroke" stroke="${halo}" stroke-width="0.7" ` +
        `stroke-linejoin="round" fill="${fill}">${sym}</text>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}" class="qicon">` +
    `<defs>` +
    `<linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${light}"/><stop offset="1" stop-color="${dark}"/></linearGradient>` +
    `<filter id="${sid}" x="-30%" y="-30%" width="160%" height="160%">` +
    `<feDropShadow dx="0" dy="0.4" stdDeviation="0.5" flood-color="#000" flood-opacity="0.35"/></filter>` +
    `</defs>` +
    `<g filter="url(#${sid})">` +
    `<circle cx="12" cy="12" r="11.5" fill="#ffffff"/>` +
    `<circle cx="12" cy="12" r="10.5" fill="url(#${gid})"/>` +
    inner +
    `</g></svg>`
  );
}
